import React, { useEffect, useState } from "react";
import SearchCard from "../../components/search/SearchCard";
import { useTranslation } from "react-i18next";
import { ImmunizationSearch, SearchStatus } from "../../models/Interfaces";
import { useNavigate, useLocation } from "react-router";
import { useOutletContext } from "react-router";
import { ErrorModal } from "../../components/modals/ErrorModal";
import "./searchList.css";
import { refreshingAtom, selectedSearchAtom, isHydrated } from "../../jotai/atoms";
import { useAtom } from "jotai";
import type { HomeContext } from "../../models/HomeContext";
import { Squirrel } from "../../components/spinner/Squirrel";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { initPullToRefresh } from "../../utils/pull-to-refresh";
import MobileFloatingLogo from "../../components/MobileFloatingLogo";
import { getDateTime } from "../../utils/data";
import { DRAWER_ID } from "../home/Home";
import { ImageIcon } from "../../components/ImageIcon";
import { useModal } from "../../context/ModalContext";
import { IconButton } from "../../components/IconButton/IconButton";

/**
 * Calculate when we should next poll for updates.
 *
 * @param timesPolled The number of times we've already polled for data
 * @returns 0 to the maximum poll time in milliseconds, or -1 if we should stop.
 */
export function getNextPollTime(timesPolled: number): number {
  const ONE_SECOND_IN_MS = 1000;
  const minDurationMs = ONE_SECOND_IN_MS;
  const minDurationPolls = 5; // We want to attempt to do some fast polling since in MOST cases the search comes back in < 5 secs
  const maxDurationMs = 60 * 60 * ONE_SECOND_IN_MS; // One poll every hour
  // This is absurdly high on purpose. It's just to prevent idle clients from infinitely driving traffic to us.
  // It takes 12 polls (17 including the min duration polls) to hit 1 hour polling time.
  // The maximum time a message can exist in the backend SQS queue is 14 days, or 336 hours.
  // 17 + 336 = 353.
  const maxPolls = 353;

  // We poll the backend as long as we're waiting on at least 1 search or record request from the backend.
  if (timesPolled < maxPolls) {
    if (timesPolled <= minDurationPolls) {
      // We're within our 'fast polling' window so throw out the previous calculation.
      return minDurationMs;
    }

    // We want an exponential decay with full jitter.
    // Canonical article from mbrooker: https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
    const expectedNextRequestTime = Math.min(
      maxDurationMs,
      minDurationMs * Math.pow(2, timesPolled - minDurationPolls)
    );
    // Add in a full jitter
    return Math.round(Math.random() * expectedNextRequestTime);
  }
  return -1;
}

export function SearchList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();

  const [curSearch, setCurSearch] = useAtom(selectedSearchAtom);
  const {
    scrollElement,
    allVerifiedRecords: allVerifiedSearches,
    allSearches,
  } = useOutletContext<HomeContext>();
  const [pullToRefreshMainElement, setPullToRefreshMainElement] = useState<HTMLDivElement | null>(
    null
  );
  const [refreshing, setRefreshing] = useAtom(refreshingAtom);
  const modal = useModal();

  useEffect(() => {
    // Do not want to refresh infinitely if reloading in the middle of a refresh
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (location.state?.openSearchModal) {
      modal.openSearchModal();
    }
  }, [location]);

  useEffect(() => {
    /**
     * If we have no searches, then we should pop open the search modal so that the user can start a new search.
     */
    if (
      allSearches.state === "hasData" &&
      isHydrated(allSearches.data) &&
      allSearches.data.length === 0
    ) {
      // TODO: The consent modal renders UNDER this :(
      //searchModal.openSearchModal();
    }
  }, [allSearches.state]);
  useEffect(() => {
    if (!scrollElement || !pullToRefreshMainElement) {
      return;
    }
    const ptr = initPullToRefresh({
      pullToRefreshMainElement,
      shouldPullToRefresh: () => {
        const dialogOpen = document.querySelector("dialog[open]") !== null;
        const drawerOpen = (document.getElementById(DRAWER_ID) as HTMLInputElement | null)?.checked;
        // iOS bouncing will cause scrollTop to be negative
        return scrollElement.scrollTop <= 0 && !dialogOpen && !drawerOpen;
      },
      onDistance: (p) => {
        // We control the PTR's position on the screen via margin-top
        p.ptrElement.style["marginTop"] = `${p.dist}px`;
        // And we also use the custom --progress property to control the image mask defined in `app.css`
        p.ptrElement.style.setProperty(
          "--progress",
          Math.min(p.dist / p.distThreshold, 1).toString()
        );
        // This is the iOS bounce fix. On iOS the scrollElement can "bounce" when scrolling past the content. In this
        // case the scrollTop can be negative, which causes the PTR to not work as expected since you would end
        // up scrolling content down BUT ALSO adding margin-top to the PTR. This effectively make it feel like
        // you're scrolling ~2x as fast which is super weird. On iOS we just rely on the bounce effect to "pull" the
        // screen down.
        if (scrollElement.scrollTop < 0) {
          p.ptrElement.style["marginTop"] = "0px";
        }
      },
      onRefresh: async () => {
        // Do not await refresh so that it happens in the background without stalling the PTR
        handleRefresh();
      },
    });
    return () => ptr.destroy();
  }, [scrollElement, pullToRefreshMainElement]);

  function getSearchHeader() {
    return (
      <div className="flex flex-col md:flex-row w-full justify-center md:justify-between not-jersey:text-primary-content jersey:max-md:text-primary-content items-center pb-4 md:pb-6 ">
        <p className="text-3xl font-bold">{t("settings.searches")}</p>
        <div className="max-md:hidden flex">
          <button
            data-testid="newSearchBtn"
            className="btn btn-primary ml-auto"
            onClick={async () => {
              await setCurSearch(null);
              modal.openSearchModal();
            }}
          >
            <ImageIcon className="w-7 h-7" src="/assets/images/Icon_Search_New_White.png" />
            {t("immunizations.search_start_new_search")}
          </button>
        </div>
      </div>
    );
  }

  const navigateToRecords = (search: ImmunizationSearch) => {
    const verifiedSearchesData =
      allVerifiedSearches.state === "hasData" && isHydrated(allVerifiedSearches.data)
        ? allVerifiedSearches.data
        : [];
    const completeRecord = verifiedSearchesData.find((s) => s.search.uid === search.uid);
    if (completeRecord?.record && completeRecord.record.immunizations.length > 0) {
      navigate("/home/records#" + verifiedSearchesData.indexOf(completeRecord));
    } else {
      navigate("/home/records");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    /**
     * Do not await this so that when we refresh we don't show a loading squirrel for the entire time it takes for the
     * backend to refresh all records from the IIS.
     */
    fetchRecordsUntilUpdated(true);
    // Give the sense of loading doing something a bit more than firing off an API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const renderSearchContent = () => {
    if (allSearches.state === "hasError") {
      setShowErrorModal(t("immunizations.search_records_error"));
      return <></>;
    }

    // If not hydrated yet, show empty list (loading spinner is handled separately)
    if (allSearches.state === "hasData" && !isHydrated(allSearches.data)) {
      return <ul></ul>;
    }

    if (
      allSearches.state === "hasData" &&
      isHydrated(allSearches.data) &&
      allSearches.data.length === 0
    ) {
      return (
        <div className={`flex flex-col gap-2`}>
          <div role="alert" className="alert">
            <div className="flex max-md:flex-col items-center gap-2">
              <ImageIcon src="/assets/images/Icon_Alert.png" className="text-primary w-7 h-7" />
              {t("immunizations.search_records_message")}
            </div>
          </div>
          <button
            data-testid="newSearchBtn-reminder"
            className="btn btn-primary md:hidden"
            onClick={async () => {
              await setCurSearch(null);
              modal.openSearchModal();
            }}
          >
            <ImageIcon className="w-7 h-7" src="/assets/images/Icon_Search_New_White.png" />
            {t("immunizations.search_start_new_search")}
          </button>
        </div>
      );
    }

    if (allSearches.state !== "hasData" || !isHydrated(allSearches.data)) return <ul></ul>;

    const searchContent = (
      <>
        {allSearches.data.map((search, index) => {
          const dateVerified = getDateTime(search.dateVerified).isValid;
          const status = search.status?.trim();

          // Determine which handlers to pass based on status
          const isVerified =
            status !== SearchStatus.noMatch &&
            status !== SearchStatus.multiMatch &&
            status !== SearchStatus.basicMatchNoContacts &&
            status !== SearchStatus.partialMatchNoContacts &&
            status !== SearchStatus.noRecords &&
            status !== SearchStatus.inQueue &&
            dateVerified;

          const isNoMatch =
            status === SearchStatus.noMatch ||
            status === SearchStatus.noRecords ||
            status === SearchStatus.multiMatch ||
            status === SearchStatus.basicMatchNoContacts ||
            status === SearchStatus.partialMatchNoContacts;

          const needsVerification =
            status === SearchStatus.partialMatchAltContacts && !dateVerified;

          return (
            <SearchCard
              key={index}
              search={search}
              index={index}
              onViewRecords={
                isVerified
                  ? () => {
                      setCurSearch(search);
                      navigateToRecords(search);
                    }
                  : undefined
              }
              onReviewSearch={
                isNoMatch || needsVerification || isVerified
                  ? async () => {
                      await setCurSearch(search);
                      modal.openSearchModal();
                    }
                  : undefined
              }
              onMoreInfo={
                isNoMatch
                  ? async () => {
                      await setCurSearch(search);
                      modal.openResultsModal();
                    }
                  : undefined
              }
              onVerifyContact={
                needsVerification
                  ? async () => {
                      await setCurSearch(search);
                      modal.openAltContactsModal();
                    }
                  : undefined
              }
            />
          );
        })}
      </>
    );

    return (
      <>
        <div data-testid="search-list" className="grid gap-4  lg:grid-cols-2">
          {searchContent}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="-mt-20 md:hidden pb-4">
        <div
          className=""
          id="pull-to-refresh-main-searches"
          ref={setPullToRefreshMainElement}
        ></div>
      </div>
      <MobileFloatingLogo />
      <div className="max-md:px-6 max-md:pb-20 max-md:pt-6">
        {(allSearches.state === "loading" ||
          refreshing ||
          (allSearches.state === "hasData" && !isHydrated(allSearches.data))) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <Squirrel />
          </div>
        )}
        {getSearchHeader()}
        {showErrorModal && (
          <ErrorModal onCloseModal={() => setShowErrorModal(false)}>
            <p>{showErrorModal}</p>
          </ErrorModal>
        )}
        <div className="section">{renderSearchContent()}</div>
      </div>
    </>
  );
}

export default SearchList;
